<template>
  <div class="ellipse-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="椭圆类型" name="type">
        <div class="ellipse-type-grid">
          <button
            type="button"
            class="ellipse-type-button"
            :class="{ active: activeMode === SketchEllipseCommand.Ellipse }"
            :disabled="!canEdit"
            @click="handleModeChange(SketchEllipseCommand.Ellipse)"
          >
            <SvgIcon :content="ellipseIcon" alt="椭圆" />
          </button>
          <button
            type="button"
            class="ellipse-type-button"
            :class="{ active: activeMode === SketchEllipseCommand.Partial }"
            :disabled="!canEdit"
            @click="handleModeChange(SketchEllipseCommand.Partial)"
          >
            <SvgIcon :content="partialEllipseIcon" alt="部分椭圆" />
          </button>
          <button
            type="button"
            class="ellipse-type-button"
            :class="{ active: activeMode === SketchEllipseCommand.Parabola }"
            :disabled="!canEdit"
            @click="handleModeChange(SketchEllipseCommand.Parabola)"
          >
            <SvgIcon :content="parabolaIcon" alt="抛物线" />
          </button>
        </div>
      </n-collapse-item>

      <n-collapse-item
        v-if="showAdvancedSections"
        title="现有几何关系"
        name="relations"
      >
        <SelectionListFrame
          :items="existingRelations"
          selection-behavior="ctrl-multiple"
          list-role="list"
          :visible-rows="4"
          context-menu-enabled
          :is-item-delete-enabled="isSketchRelationSnapshotDeleteEnabled"
          :show-delete-all-action="existingRelations.some(isSketchRelationSnapshotDeleteEnabled)"
          item-key-field="relationId"
          @delete-item="handleDeleteRelationSelection"
          @delete-all="handleDeleteAllRelations"
        >
          <template #item="{ item }">
            <SketchRelationDiagnosticText
              :item="item"
              :label="resolveRelationLabel(item)"
            />
          </template>
        </SelectionListFrame>
      </n-collapse-item>

      <n-collapse-item
        v-if="showAdvancedSections"
        title="添加几何关系"
        name="add-relations"
      >
        <div class="add-relations-section">
          <n-space size="small" wrap>
            <n-button
              v-for="relation in availableRelations"
              :key="relation.relationType"
              size="small"
              quaternary
              :disabled="relation.disabled"
              @click="handleAddRelation(relation.relationType)"
            >
              {{ relation.label }}
            </n-button>
          </n-space>
        </div>
      </n-collapse-item>

      <n-collapse-item title="选项" name="options">
        <div class="option-list">
          <n-checkbox
            :checked="constructionChecked"
            :disabled="!canEdit"
            @update:checked="handleForConstruction"
          >
            作为构造线
          </n-checkbox>
          <n-checkbox
            v-if="showConstructionLineOption"
            :checked="options.addConstructionLines"
            :disabled="!canEdit"
            @update:checked="handleConstructionLines"
          >
            添加构造线
          </n-checkbox>
        </div>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <div class="param-grid">
          <div
            v-for="row in parameterRows"
            :key="row.key"
            class="param-row"
          >
            <n-text depth="3" class="param-label">{{ row.label }}</n-text>
            <n-input-number
              :value="row.value ?? null"
              size="small"
              :disabled="row.disabled"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="row.onUpdate"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Subscription } from 'rxjs';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NSpace,
  NText,
} from 'naive-ui';
import type {
  EllipseOptions,
  EllipseParameters,
  EllipseParameterUpdate,
  PropertyManagerContext,
} from '@/core';
import * as THREE from 'three';
import { useGetSelectEllipse, useViewer } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import SketchRelationDiagnosticText from '../common/SketchRelationDiagnosticText.vue';
import {isSketchRelationSnapshotDeleteEnabled} from '@/utils/sketch/isSketchRelationSnapshotDeleteEnabled';
import {
  type AvailableRelationButtonViewModel,
  isSketchParabolaObject,
  roundToEightDecimalPlaces,
  SketchEllipseCommand,
  type SketchEllipseObject,
  type SketchParabolaObject,
  type SketchRelationKind,
  type SketchRelationSnapshot,
  
  convertMmToM,
  convertMToMm,
  
} from '@puppycad/cad-core';
import SvgIcon from '@/components/common/SvgIcon.vue';
import ellipseIcon from '@/assets/icon/ellipse.svg?raw';
import partialEllipseIcon from '@/assets/icon/ellipse_partial.svg?raw';
import parabolaIcon from '@/assets/icon/ellipse_parabola.svg?raw';

defineOptions({
  inheritAttrs: false,
});

interface EllipsePropertySectionProps {
  context?: PropertyManagerContext;
}

type SelectedEllipseFamilyObject = SketchEllipseObject | SketchParabolaObject;
type ParabolaEndpointRole = 'start' | 'end';
type ParabolaAnchorRole = 'center' | 'apex';
type ParameterRow = {
  key: string;
  label: string;
  value: number | null | undefined;
  disabled: boolean;
  onUpdate: (value: number | null) => void;
};

const props = defineProps<EllipsePropertySectionProps>();

const defaultOptions: EllipseOptions = {
  mode: SketchEllipseCommand.Ellipse,
  forConstruction: false,
  addConstructionLines: false,
};

const { getViewer } = useViewer();
const {
  getSelected: getSelectedEllipse,
  onChanged: onEllipseChanged,
} = useGetSelectEllipse();

const localEllipseOptions = ref<EllipseOptions>({ ...defaultOptions });
const localEllipseParameters = ref<EllipseParameters | null>(null);
const isEllipseSelected = ref(false);
const existingRelations = ref<SketchRelationSnapshot[]>([]);
const availableRelations = ref<AvailableRelationButtonViewModel[]>([]);
const constructionChecked = ref(false);

const ellipseChangedSub = onEllipseChanged().subscribe((ellipse) => {
  isEllipseSelected.value = ellipse !== null;
  refreshPanelState(ellipse);
  syncConstructionCheckedState(ellipse);
  syncParametersFromSelectedConic(ellipse);
});

let ellipseParamsSub: Subscription | null = null;

onMounted(() => {
  const initialEllipse = getSelectedEllipse();
  isEllipseSelected.value = initialEllipse !== null;
  refreshPanelState(initialEllipse);
  syncConstructionCheckedState(initialEllipse);
  syncParametersFromSelectedConic(initialEllipse);
  const ellipseDrawingParameters$ = getViewer()?.observeEllipseDrawingParameters?.();
  if (!ellipseDrawingParameters$) {
    return;
  }
  ellipseParamsSub = ellipseDrawingParameters$.subscribe((params) => {
    if (params) {
      localEllipseParameters.value = params;
    }
    syncConstructionCheckedState(getSelectedEllipse());
  });
});

onBeforeUnmount(() => {
  ellipseChangedSub.unsubscribe();
  ellipseParamsSub?.unsubscribe();
});

const canEdit = computed(() => Boolean(getViewer()));
const showAdvancedSections = computed(() => isEllipseSelected.value);
const options = computed(() => localEllipseOptions.value);
const ellipseParams = computed(() => localEllipseParameters.value);
const defaultExpanded = ['type', 'parameters', 'options'];

function resolveDrawingCommandMode(command: string | null | undefined): SketchEllipseCommand | null {
  if (
    command === SketchEllipseCommand.Ellipse
    || command === SketchEllipseCommand.Partial
    || command === SketchEllipseCommand.Parabola
  ) {
    return command;
  }
  return null;
}

watch(
  () => props.context?.currentDrawingCommand,
  (command) => {
    const drawingMode = resolveDrawingCommandMode(command);
    if (!drawingMode || isEllipseSelected.value) {
      return;
    }
    localEllipseOptions.value = {
      ...localEllipseOptions.value,
      mode: drawingMode,
    };
  },
  { immediate: true },
);

function isSelectedParabola(
  target: SelectedEllipseFamilyObject | null,
): target is SketchParabolaObject {
  return target !== null && isSketchParabolaObject(target);
}

function resolveSelectedEllipseMode(
  ellipse: SelectedEllipseFamilyObject,
): SketchEllipseCommand {
  if (isSelectedParabola(ellipse)) {
    return SketchEllipseCommand.Parabola;
  }
  const definition = ellipse.getEllipseDefinition();
  const angleSpan = Math.abs(definition.endAngle - definition.startAngle);
  const normalizedSpan = angleSpan % (Math.PI * 2);
  const isPartial = normalizedSpan > 1e-6
    && Math.abs(normalizedSpan - Math.PI * 2) > 1e-6;
  return isPartial ? SketchEllipseCommand.Partial : SketchEllipseCommand.Ellipse;
}

const activeMode = computed<SketchEllipseCommand>(() => {
  if (isEllipseSelected.value) {
    const selectedEllipse = getSelectedEllipse();
    if (selectedEllipse) {
      return resolveSelectedEllipseMode(selectedEllipse);
    }
  }
  return options.value.mode;
});

const canEditParameters = computed(() => {
  if (!isEllipseSelected.value || !getViewer()) {
    return false;
  }
  return activeMode.value !== SketchEllipseCommand.Conic;
});

const showConstructionLineOption = computed(
  () => activeMode.value === SketchEllipseCommand.Ellipse,
);

function syncConstructionCheckedState(
  ellipse: ReturnType<typeof getSelectedEllipse>,
): void {
  if (ellipse) {
    constructionChecked.value = Boolean(ellipse.isConstruction);
    return;
  }
  constructionChecked.value = Boolean(localEllipseOptions.value.forConstruction);
}


/**
 * 更新椭圆绘制选项。
 * @param patch 椭圆绘制选项增量。
 */
function updateOptions(patch: Partial<EllipseOptions>): void {
  const renderer = getViewer();
  const currentOptions = renderer?.getEllipseDrawingOptions?.();
  if (!renderer || !currentOptions) {
    return;
  }
  const nextOptions = { ...currentOptions, ...patch };
  if (renderer.setEllipseDrawingOptions) {
    renderer.setEllipseDrawingOptions(nextOptions);
  } else {
    renderer.setEllipseDrawingMode?.(String(nextOptions.mode));
  }
  localEllipseOptions.value = { ...localEllipseOptions.value, ...nextOptions };
  syncConstructionCheckedState(getSelectedEllipse());
}

/**
 * 处理椭圆模式切换。
 * @param mode 目标椭圆绘制模式。
 */
function handleModeChange(mode: SketchEllipseCommand): void {
  updateOptions({ mode });
}


function handleForConstruction(value: boolean): void {
  if (isEllipseSelected.value) {
    const selectedEllipse = getSelectedEllipse();
    if (selectedEllipse) {
      selectedEllipse.isConstruction = value;
      const viewer = getViewer();
      viewer?.syncObjectBaseVisualState?.(selectedEllipse);
      if (value || !viewer?.shadedSketchContoursEnabled) {
        if ('clearContourDisplay' in selectedEllipse) {
          selectedEllipse.clearContourDisplay?.();
        }
      } else {
        if ('syncContour' in selectedEllipse) {
          selectedEllipse.syncContour?.();
        }
      }
      finalizeEllipsePropertyEdit(selectedEllipse);
      constructionChecked.value = value;
      syncConstructionCheckedState(selectedEllipse);
      syncParametersFromSelectedConic(selectedEllipse);
      return;
    }
  }
  constructionChecked.value = value;
  updateOptions({ forConstruction: value });
}

function handleConstructionLines(value: boolean): void {
  updateOptions({ addConstructionLines: value });
}

function syncParametersFromSelectedConic(
  ellipse: SelectedEllipseFamilyObject | null,
): void {
  if (!ellipse) {
    localEllipseParameters.value = null;
    return;
  }

  const round = (value: number): number => roundToEightDecimalPlaces(value);

  if (isSelectedParabola(ellipse)) {
    const center = ellipse.getFocalPoint().getLocalPosition2D();
    const apex = ellipse.getApexPoint().getLocalPosition2D();
    const start = ellipse.getStartPoint().getLocalPosition2D();
    const end = ellipse.getEndPoint().getLocalPosition2D();
    localEllipseParameters.value = {
      anchor1_2d: [round(center.x), round(center.y)],
      anchor2_2d: [round(apex.x), round(apex.y)],
      start2d: [round(start.x), round(start.y)],
      end2d: [round(end.x), round(end.y)],
    };
    return;
  }

  const definition = ellipse.getEllipseDefinition();
  const localCenter = ellipse.referencePlane.worldToLocal(definition.centerWorld);

  const params: EllipseParameters = {
    center: definition.centerWorld.toArray() as [number, number, number],
    center2d: [round(localCenter.x), round(localCenter.y)],
    radiusX: round(definition.radiusX),
    radiusY: round(definition.radiusY),
    rotation: round((definition.rotation * 180) / Math.PI),
  };

  const angleSpan = Math.abs(definition.endAngle - definition.startAngle);
  const normalizedSpan = angleSpan % (Math.PI * 2);
  const isPartial = normalizedSpan > 1e-6
    && Math.abs(normalizedSpan - Math.PI * 2) > 1e-6;

  if (isPartial) {
    params.startAngle = round((definition.startAngle * 180) / Math.PI);
    params.endAngle = round((definition.endAngle * 180) / Math.PI);
    const arcAngle = definition.clockwise
      ? definition.startAngle - definition.endAngle
      : definition.endAngle - definition.startAngle;
    params.arcAngle = round(
      ((arcAngle < 0 ? arcAngle + Math.PI * 2 : arcAngle) * 180) / Math.PI,
    );
  }

  localEllipseParameters.value = params;
  getViewer()?.setEllipseDrawingParameters?.(params);
}

function finalizeEllipsePropertyEdit(
  ellipse: SelectedEllipseFamilyObject | null,
): void {
  if (!ellipse) {
    return;
  }
  props.context?.finalizeSketchPropertyEdit?.(ellipse, {
    reason: 'property-edit',
    sourceObjectId: ellipse.uuid,
  });
}


/**
 * 刷新当前选中椭圆的可添加几何关系列表。
 * @param ellipse 当前椭圆族选中对象，传入 null 时清空按钮列表。
 */
function refreshAvailableRelations(
  ellipse: SelectedEllipseFamilyObject | null,
): void {
  if (!ellipse || isSelectedParabola(ellipse)) {
    availableRelations.value = [];
    return;
  }

  // 当前关系创建服务支持椭圆对象；抛物线继续复用面板，但不显示未支持的关系按钮。
  availableRelations.value = getViewer()?.sketchRelationCreationService.build(ellipse) ?? [];
}

/**
 * 刷新椭圆属性面板中的几何关系状态。
 * @param ellipse 当前椭圆族选中对象，传入 null 时清空关系展示状态。
 */
function refreshPanelState(
  ellipse: SelectedEllipseFamilyObject | null,
): void {
  refreshExistingRelations(ellipse);
  refreshAvailableRelations(ellipse);
}

/**
 * 处理椭圆属性面板中的添加几何关系操作。
 * @param relationType 需要创建的几何关系类型。
 */
function handleAddRelation(relationType: SketchRelationKind): void {
  const selectedEllipse = getSelectedEllipse();
  const viewer = getViewer();

  if (!selectedEllipse || isSelectedParabola(selectedEllipse) || !viewer) {
    return;
  }

  // 创建动作统一下沉到 cad-core，避免 UI 分支重复实现约束绑定规则。
  const created = viewer.sketchRelationCreationService.createRelation({
    objects: [selectedEllipse],
    relationKind: relationType,
  });
  if (!created) {
    return;
  }

  refreshPanelState(selectedEllipse);
}


function refreshExistingRelations(
  ellipse: SelectedEllipseFamilyObject | null,
): void {
  if (!ellipse) {
    existingRelations.value = [];
    return;
  }
  existingRelations.value = ellipse.constraints.getRelationSnapshots();
}

function resolveRelationLabel(item: unknown): string {
  const snapshot = item as SketchRelationSnapshot | null;
  if (!snapshot) {
    return '';
  }
  return snapshot.baseDisplayName || snapshot.name || String(snapshot.relationType);
}

async function removeEllipseRelation(
  ellipse: NonNullable<ReturnType<typeof getSelectedEllipse>>,
  relationId: number,
): Promise<boolean> {
  return await props.context?.removeSketchRelation?.(ellipse, relationId) ?? false;
}

async function handleDeleteRelationSelection(payload: {
  item: unknown;
  index: number;
}): Promise<void> {
  const ellipse = getSelectedEllipse();
  if (!ellipse) {
    return;
  }
  const snapshot = payload.item as SketchRelationSnapshot;
  if (!snapshot || !Number.isFinite(snapshot.id) || !isSketchRelationSnapshotDeleteEnabled(snapshot)) {
    return;
  }
  if (!await removeEllipseRelation(ellipse, snapshot.id)) {
    return;
  }
  getViewer()?.removeObjectConstraintMarker?.(ellipse, snapshot.id);
  refreshPanelState(ellipse);
}

async function handleDeleteAllRelations(): Promise<void> {
  const ellipse = getSelectedEllipse();
  if (!ellipse) {
    return;
  }
  const viewer = getViewer();
  for (const snapshot of existingRelations.value.filter(isSketchRelationSnapshotDeleteEnabled)) {
    if (!await removeEllipseRelation(ellipse, snapshot.id)) {
      return;
    }
    viewer?.removeObjectConstraintMarker?.(ellipse, snapshot.id);
  }
  refreshPanelState(ellipse);
}

function handleCenterInput(axis: 'x' | 'y', value: number | null): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  
  // 用户输入的毫米值转为米传给底层
  updateSelectedEllipse({ type: 'center', axis, value: convertMmToM(value) });
  
}

function handleRadiusXInput(value: number | null): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  
  // 用户输入的毫米值转为米传给底层
  updateSelectedEllipse({ type: 'radiusX', value: convertMmToM(value) });
  
}

function handleRadiusYInput(value: number | null): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  
  // 用户输入的毫米值转为米传给底层
  updateSelectedEllipse({ type: 'radiusY', value: convertMmToM(value) });
  
}

function handleRotationInput(value: number | null): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  updateSelectedEllipse({ type: 'rotation', value });
}

function handleStartAngleInput(value: number | null): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  updateSelectedEllipse({ type: 'startAngle', value });
}

function handleEndAngleInput(value: number | null): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  updateSelectedEllipse({ type: 'endAngle', value });
}

function updateSelectedParabolaEndpoint(
  role: ParabolaEndpointRole,
  axis: 'x' | 'y',
  value: number,
): void {
  const selectedEllipse = getSelectedEllipse();
  if (!selectedEllipse || !isSelectedParabola(selectedEllipse)) {
    return;
  }
  const updated = selectedEllipse.updateEndpointCoordinateOnReferencePlane(
    role,
    axis,
    value,
  );
  if (!updated) {
    return;
  }
  finalizeEllipsePropertyEdit(selectedEllipse);
  syncParametersFromSelectedConic(selectedEllipse);
}

function updateSelectedParabolaAnchor(
  role: ParabolaAnchorRole,
  axis: 'x' | 'y',
  value: number,
): void {
  const selectedEllipse = getSelectedEllipse();
  if (!selectedEllipse || !isSelectedParabola(selectedEllipse)) {
    return;
  }
  const controlPoint = role === 'center'
    ? selectedEllipse.getFocalPoint()
    : selectedEllipse.getApexPoint();
  controlPoint.setCoordinateOnReferencePlane(axis, value);
  finalizeEllipsePropertyEdit(selectedEllipse);
  syncParametersFromSelectedConic(selectedEllipse);
}

function handleParabolaEndpointInput(
  role: ParabolaEndpointRole,
  axis: 'x' | 'y',
  value: number | null,
): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  
  // 用户输入的毫米值转为米传给底层
  updateSelectedParabolaEndpoint(role, axis, convertMmToM(value));
  
}

function handleParabolaAnchorInput(
  role: ParabolaAnchorRole,
  axis: 'x' | 'y',
  value: number | null,
): void {
  if (!canEditParameters.value || value === null || Number.isNaN(value)) {
    return;
  }
  
  // 用户输入的毫米值转为米传给底层
  updateSelectedParabolaAnchor(role, axis, convertMmToM(value));
  
}

function updateEllipseConstructionLines(
  _renderer: ReturnType<typeof useViewer> extends { getViewer: () => infer R }
    ? R
    : never,
  _ellipseObject: THREE.Object3D,
  centerWorld: THREE.Vector3,
  radiusX: number,
  radiusY: number,
  rotation: number,
): void {
  void centerWorld;
  void radiusX;
  void radiusY;
  void rotation;
}

function updateSelectedEllipse(update: EllipseParameterUpdate): void {
  const renderer = getViewer();
  const selectedEllipse = getSelectedEllipse();
  if (!renderer || !selectedEllipse || isSelectedParabola(selectedEllipse)) {
    return;
  }

  const definition = selectedEllipse.getEllipseDefinition();
  const mode = resolveSelectedEllipseMode(selectedEllipse);
  if (mode === SketchEllipseCommand.Conic) {
    return;
  }

  const referencePlane = selectedEllipse.referencePlane;
  const centerWorld = definition.centerWorld.clone();
  const currentCenter2d = localEllipseParameters.value?.center2d
    ? new THREE.Vector2(
      localEllipseParameters.value.center2d[0],
      localEllipseParameters.value.center2d[1],
    )
    : referencePlane.worldToLocal(centerWorld);

  const nextCenter2d = currentCenter2d.clone();
  let nextRadiusX = definition.radiusX;
  let nextRadiusY = definition.radiusY;
  let nextRotation = definition.rotation;
  let startAngle = definition.startAngle;
  let endAngle = definition.endAngle;

  if (update.type === 'center') {
    if (update.axis === 'x') {
      nextCenter2d.x = update.value;
    } else {
      nextCenter2d.y = update.value;
    }
  } else if (update.type === 'radiusX') {
    nextRadiusX = update.value;
  } else if (update.type === 'radiusY') {
    nextRadiusY = update.value;
  } else if (update.type === 'rotation') {
    nextRotation = (update.value * Math.PI) / 180;
  } else if (update.type === 'startAngle') {
    startAngle = (update.value * Math.PI) / 180;
  } else if (update.type === 'endAngle') {
    endAngle = (update.value * Math.PI) / 180;
  }

  if (!Number.isFinite(nextRadiusX) || nextRadiusX <= 0) {
    return;
  }
  if (!Number.isFinite(nextRadiusY) || nextRadiusY <= 0) {
    return;
  }

  const newCenterWorld = referencePlane.localToWorld(nextCenter2d);
  const normalizeEllipseAngle = (value: number) => {
    const twoPi = Math.PI * 2;
    let angle = value % twoPi;
    if (angle < 0) {
      angle += twoPi;
    }
    return angle;
  };

  let normalizedStart = startAngle ?? 0;
  let normalizedEnd = endAngle ?? 0;
  const clockwise = mode === SketchEllipseCommand.Partial
    ? definition.clockwise
    : false;

  if (
    mode === SketchEllipseCommand.Partial
    && typeof startAngle === 'number'
    && typeof endAngle === 'number'
  ) {
    normalizedStart = normalizeEllipseAngle(startAngle);
    normalizedEnd = normalizeEllipseAngle(endAngle);
  } else {
    normalizedStart = 0;
    normalizedEnd = Math.PI * 2;
  }

  selectedEllipse.updateEllipseDefinition({
    centerWorld: newCenterWorld,
    radiusX: nextRadiusX,
    radiusY: nextRadiusY,
    rotation: nextRotation,
    startAngle: mode === SketchEllipseCommand.Partial ? normalizedStart : 0,
    endAngle: mode === SketchEllipseCommand.Partial ? normalizedEnd : Math.PI * 2,
    clockwise,
    segments: definition.segments,
  });

  updateEllipseConstructionLines(
    renderer,
    selectedEllipse,
    newCenterWorld,
    nextRadiusX,
    nextRadiusY,
    nextRotation,
  );

  finalizeEllipsePropertyEdit(selectedEllipse);
  syncParametersFromSelectedConic(selectedEllipse);
}


const parameterRows = computed<ParameterRow[]>(() => {
  const params = ellipseParams.value;

  if (activeMode.value === SketchEllipseCommand.Ellipse) {
    return [
      {
        key: 'center-x',
        label: 'X',
        value: params?.center2d?.[0] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.center2d[0])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleCenterInput('x', value),
      },
      {
        key: 'center-y',
        label: 'Y',
        value: params?.center2d?.[1] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.center2d[1])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleCenterInput('y', value),
      },
      {
        key: 'radius-x',
        label: '长轴',
        value: (params?.radiusX !== undefined && params?.radiusX !== null) ? roundToEightDecimalPlaces(convertMToMm(params.radiusX)) : null,
        disabled: !canEditParameters.value,
        onUpdate: handleRadiusXInput,
      },
      {
        key: 'radius-y',
        label: '短轴',
        value: (params?.radiusY !== undefined && params?.radiusY !== null) ? roundToEightDecimalPlaces(convertMToMm(params.radiusY)) : null,
        disabled: !canEditParameters.value,
        onUpdate: handleRadiusYInput,
      },
      {
        key: 'rotation',
        label: '角度',
        value: params?.rotation,
        disabled: !canEditParameters.value,
        onUpdate: handleRotationInput,
      },
    ];
  }

  if (activeMode.value === SketchEllipseCommand.Partial) {
    return [
      {
        key: 'center-x',
        label: 'X',
        value: params?.center2d?.[0] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.center2d[0])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleCenterInput('x', value),
      },
      {
        key: 'center-y',
        label: 'Y',
        value: params?.center2d?.[1] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.center2d[1])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleCenterInput('y', value),
      },
      {
        key: 'radius-x',
        label: '长轴',
        value: (params?.radiusX !== undefined && params?.radiusX !== null) ? roundToEightDecimalPlaces(convertMToMm(params.radiusX)) : null,
        disabled: !canEditParameters.value,
        onUpdate: handleRadiusXInput,
      },
      {
        key: 'radius-y',
        label: '短轴',
        value: (params?.radiusY !== undefined && params?.radiusY !== null) ? roundToEightDecimalPlaces(convertMToMm(params.radiusY)) : null,
        disabled: !canEditParameters.value,
        onUpdate: handleRadiusYInput,
      },
      {
        key: 'start-angle',
        label: '起始角',
        value: params?.startAngle,
        disabled: !canEditParameters.value,
        onUpdate: handleStartAngleInput,
      },
      {
        key: 'end-angle',
        label: '终止角',
        value: params?.endAngle,
        disabled: !canEditParameters.value,
        onUpdate: handleEndAngleInput,
      },
      {
        key: 'arc-angle',
        label: '弧角',
        value: params?.arcAngle,
        disabled: true,
        onUpdate: () => undefined,
      },
      {
        key: 'rotation',
        label: '角度',
        value: params?.rotation,
        disabled: !canEditParameters.value,
        onUpdate: handleRotationInput,
      },
    ];
  }

  if (activeMode.value === SketchEllipseCommand.Parabola) {
    return [
      {
        key: 'start-x',
        label: '起点 X',
        value: params?.start2d?.[0] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.start2d[0])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleParabolaEndpointInput('start', 'x', value),
      },
      {
        key: 'start-y',
        label: '起点 Y',
        value: params?.start2d?.[1] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.start2d[1])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleParabolaEndpointInput('start', 'y', value),
      },
      {
        key: 'end-x',
        label: '终点 X',
        value: params?.end2d?.[0] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.end2d[0])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleParabolaEndpointInput('end', 'x', value),
      },
      {
        key: 'end-y',
        label: '终点 Y',
        value: params?.end2d?.[1] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.end2d[1])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleParabolaEndpointInput('end', 'y', value),
      },
      {
        key: 'center-x',
        label: '中心 X',
        value: params?.anchor1_2d?.[0] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.anchor1_2d[0])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleParabolaAnchorInput('center', 'x', value),
      },
      {
        key: 'center-y',
        label: '中心 Y',
        value: params?.anchor1_2d?.[1] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.anchor1_2d[1])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleParabolaAnchorInput('center', 'y', value),
      },
      {
        key: 'apex-x',
        label: '顶点 X',
        value: params?.anchor2_2d?.[0] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.anchor2_2d[0])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleParabolaAnchorInput('apex', 'x', value),
      },
      {
        key: 'apex-y',
        label: '顶点 Y',
        value: params?.anchor2_2d?.[1] !== undefined ? roundToEightDecimalPlaces(convertMToMm(params.anchor2_2d[1])) : null,
        disabled: !canEditParameters.value,
        onUpdate: (value) => handleParabolaAnchorInput('apex', 'y', value),
      },
    ];
  }

  return [];
});

</script>

<style scoped>
.ellipse-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.ellipse-type-grid {
  display: grid;
  grid-template-columns: repeat(2, 48px);
  gap: 8px;
}

.ellipse-type-button {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
}

.ellipse-type-button :deep(.puppy-svg-icon) {
  width: 28px;
  height: 28px;
}

.ellipse-type-button.active {
  border-color: rgba(66, 153, 225, 0.8);
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.4) inset;
}

.ellipse-type-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.add-relations-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 46px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}
</style>
